<template>
  <side-bar :selectedView="selectedView" @item-selected="onNavItemSelected" />
  <div class="col-xxl-10 offset-xxl-2 col-xl-9 offset-xl-3">
      <div class="m-lg-5 mt-5">
          <dashboard v-if="showDashboard"
                     :showPageNav="false"
                     headerText="Our dashboard tracker below shows you everything you have to use and if your plan is still expected to work under various scenarios. You can edit anything you have here. On the left hand side, there is a menu for help on various elements, which can help you improve your retirement outcome."
                     @guaranteed-income-change-required="onShowGuaranteedIncomeEditor">
          </dashboard>

          <pension-management v-if="showPensionManagement"></pension-management>

          <providers ref="providers"
                     v-show="showProviders"
                     @redirect-required="onRedirectFromProviders" />
          <advisers v-if="showAdvisers" />
          <guaranteedIncome ref="guaranteedIncome" v-show="showGuaranteedIncome" />
          <statePensionHelp ref="statePension" v-show="showStatePensionHelp" />
          <lumpsSumsAndTax v-if="showLumpsSumsAndTax" />
          <benefitsAndCredits v-if="showBenefitsAndCredits" />
          <equityRelease v-if="showEquityRelease" />
          <partnersIncome v-if="showPartnersIncome" />
          <helpAndEducation v-if="showHelpAndEduction" />
          <otherWaysToSave v-if="showOtherWaysToSave" />
          <financialCoaching v-if="showFinancialCoaching" />
          <whyPayMore v-if="showWhyPayMore" />
      </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import SideBar from "@/guiide/components/common/SideBar.vue";
import GuiidePlusView from "@/guiide/ts/guiideplus/guiidePlusView";
import Dashboard from "@/guiide/components/Dashboard.vue";
import PensionManagement from "./guiideplus/PensionManagement.vue";
import Providers from "@/guiide/components/guiideplus/providers/Providers.vue";
import Advisers from "@/guiide/components/guiideplus/Advisers.vue";
import GuaranteedIncome from "@/guiide/components/guiideplus/GuaranteedIncome.vue";
import StatePensionHelp from "@/guiide/components/guiideplus/StatePensionHelp.vue";
import LumpsSumsAndTax from "@/guiide/components/guiideplus/LumpSumsAndTax.vue";
import BenefitsAndCredits from "@/guiide/components/guiideplus/Benefits.vue";
import EquityRelease from "@/guiide/components/guiideplus/EquityRelease.vue";
import PartnersIncome from "@/guiide/components/guiideplus/PartnersIncome.vue";
import HelpAndEducation from "@/guiide/components/guiideplus/HelpAndEducation.vue";
import OtherWaysToSave from "@/guiide/components/guiideplus/OtherWaysToSave.vue";
import FinancialCoaching from "@/guiide/components/guiideplus/FinancialCoaching.vue";
import WhyPayMore from "@/guiide/components/guiideplus/WhyPayMore.vue";
import { Configuration } from "../ts/configuration/configuration";
import { guiidePlusMonitoring } from "@/guiide/ts/guiideplus/guiidePlusMonitoring";

export default defineComponent({
  name: "GuiidePlus",
  components: {
    SideBar,
    Dashboard,
    PensionManagement,
    Providers,
    Advisers,
    GuaranteedIncome,
    StatePensionHelp,
    LumpsSumsAndTax,
    BenefitsAndCredits,
    EquityRelease,
    PartnersIncome,
    HelpAndEducation,
    OtherWaysToSave,
    FinancialCoaching,
    WhyPayMore
  },
  setup() {
    const providers = ref(null);
    const guaranteedIncome = ref(null);
    const statePension = ref(null);

    return {
      providers,
      guaranteedIncome,
      statePension,
    };
  },
  data() {
    return {
      initialLoad: true,
      selectedView: GuiidePlusView.Dashboard,
    };
  },
  mounted() {
    if (this.initialLoad) {
      const config: Configuration = this.$store.state.configuration;
      if (config.section == "p") {
        this.selectedView = GuiidePlusView.Providers;
      } else if (config.section == "b") {
        this.selectedView = GuiidePlusView.BenefitsAndCredits;
      } else if (config.section == "wpm") {
        this.selectedView = GuiidePlusView.WhyPayMore;
      } else if (config.section == "sp") {
        this.selectedView = GuiidePlusView.StatePensionHelp;
      } else if (config.section == "ows") {
        this.selectedView = GuiidePlusView.OtherWaysToSave;
      } else if (config.section == "fc") {
        this.selectedView = GuiidePlusView.FinancialCoaching;
      } else if (config.section == "he") {
          this.selectedView = GuiidePlusView.HelpAndEduction;
      }
      this.initialLoad = false;
    }
  },
  methods: {
    onNavItemSelected(navItem: GuiidePlusView) {
      this.selectedView = navItem;
      if (navItem == GuiidePlusView.Providers) {
        if (this.providersShowsRetailers) {
          this.providers.showProvidersView();
        } else {
          this.providers.showSimpleComparisonView();
        }
      } else if (navItem == GuiidePlusView.StatePensionHelp) {
        this.statePension.reset();
      } else if (navItem == GuiidePlusView.GuaranteedIncome) {
        this.guaranteedIncome.reset();
      }
      guiidePlusMonitoring.logView(navItem);
    },
    onRedirectFromProviders(redirectTarget: GuiidePlusView) {
      this.selectedView = redirectTarget;
      guiidePlusMonitoring.logView(redirectTarget);
      if (redirectTarget == GuiidePlusView.GuaranteedIncome) {
        this.guaranteedIncome.displayModellingOutcome();
      }
    },
    onShowGuaranteedIncomeEditor() {
      this.selectedView = GuiidePlusView.GuaranteedIncome;
      this.guaranteedIncome.showEditor();
    },
  },
  computed: {
    showDashboard() {
      return this.selectedView == GuiidePlusView.Dashboard;
    },
    showPensionManagement() {
      return this.selectedView == GuiidePlusView.PensionManagement;
    },
    showProviders() {
      return this.selectedView == GuiidePlusView.Providers;
    },
    showAdvisers() {
      return this.selectedView == GuiidePlusView.Advisers;
    },
    showGuaranteedIncome() {
      return this.selectedView == GuiidePlusView.GuaranteedIncome;
    },
    showStatePensionHelp() {
      return this.selectedView == GuiidePlusView.StatePensionHelp;
    },
    showLumpsSumsAndTax() {
      return this.selectedView == GuiidePlusView.LumpsSumsAndTax;
    },
    showBenefitsAndCredits() {
      return this.selectedView == GuiidePlusView.BenefitsAndCredits;
    },
    showEquityRelease() {
      return this.selectedView == GuiidePlusView.EquityRelease;
    },
    showPartnersIncome() {
      return this.selectedView == GuiidePlusView.PartnersIncome;
    },
    showHelpAndEduction() {
      return this.selectedView == GuiidePlusView.HelpAndEduction;
    },
    showWhyPayMore() {
      return this.selectedView == GuiidePlusView.WhyPayMore;
    },
    showOtherWaysToSave() {
      return this.selectedView == GuiidePlusView.OtherWaysToSave;
    },
    showFinancialCoaching() {
      return this.selectedView == GuiidePlusView.FinancialCoaching;
    },
    providersShowsRetailers() {
      const config: Configuration = this.$store.state.configuration;
      return config.providersShowsRetailers;
    }
  },
});
</script>
